/**
 * Focus machinery for the dialog rendered by `Modal.vue`.
 *
 * `role="dialog"` plus `aria-modal="true"` is a promise, not a label: it tells
 * assistive technology that everything outside the node is inert. The browser
 * does not enforce that for us -- `aria-modal` hides the background from a
 * screen reader's virtual cursor but does nothing to the Tab order, so a
 * dialog that carries the attribute without trapping Tab actively lies. This
 * module is the enforcement, so the role can be applied honestly.
 *
 * Split out of the component for the same reason `menuKeyboard.js` was: it is
 * DOM machinery with no Vue in it, it is the part worth testing directly, and
 * `Modal.vue` is already a 220-line template with eighteen props.
 *
 * Everything here reads the DOM at keypress time rather than caching a list at
 * open. Baserow's modals are built out of `v-if`-ed permission checks, async
 * `<component :is>` slots and collapsible sidebars, so the set of controls a
 * user can reach changes between one Tab and the next.
 */

/**
 * Things that can hold focus.
 *
 * `[tabindex]` is matched broadly and then filtered on the parsed value below,
 * because a negative tabindex is focusable by script but must stay out of the
 * Tab sequence -- including it here would make the trap wrap onto an element
 * the browser would never have tabbed to.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button',
  'input',
  'select',
  'textarea',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]',
  '[tabindex]',
].join(',')

/**
 * Whether the element is hidden from the user, and so from the Tab order.
 *
 * jsdom does not lay out, so `offsetParent` is null for everything and
 * `getClientRects()` is always empty there. Neither can be used as the
 * primary signal without every element counting as hidden under test. The
 * `hidden` attribute, inline `display: none`/`visibility: hidden` and the
 * `visibility-hidden` class the codebase uses on overlays are checked
 * directly instead, walking ancestors so a control inside a collapsed
 * sidebar is excluded along with the sidebar.
 *
 * @param {HTMLElement} element
 * @returns {boolean}
 */
const isHidden = (element) => {
  let node = element
  while (node && node.nodeType === 1) {
    if (node.hidden) {
      return true
    }
    const style = node.style
    if (style && (style.display === 'none' || style.visibility === 'hidden')) {
      return true
    }
    if (node.classList && node.classList.contains('visibility-hidden')) {
      return true
    }
    node = node.parentElement
  }
  return false
}

/**
 * Whether the element is one the user can actually Tab to.
 *
 * @param {HTMLElement} element
 * @returns {boolean}
 */
const isTabbable = (element) => {
  if (element.disabled === true) {
    return false
  }
  // `disabled` on a `<fieldset>` takes its descendants out of the Tab order
  // too, and the property is not mirrored onto them.
  if (element.closest('fieldset[disabled]')) {
    return false
  }
  if (element.getAttribute('aria-disabled') === 'true') {
    return false
  }
  // `type="hidden"` inputs match the selector but are not controls at all.
  if (element.tagName === 'INPUT' && element.type === 'hidden') {
    return false
  }
  const tabindex = element.getAttribute('tabindex')
  if (tabindex !== null && Number.parseInt(tabindex, 10) < 0) {
    return false
  }
  if (isHidden(element)) {
    return false
  }
  return true
}

/**
 * Every element inside `rootEl` the user can Tab to, in DOM order.
 *
 * @param {HTMLElement|null} rootEl
 * @returns {HTMLElement[]}
 */
export const getFocusableElements = (rootEl) => {
  if (!rootEl) {
    return []
  }
  return Array.from(rootEl.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    isTabbable
  )
}

/**
 * Moves focus into the dialog.
 *
 * Falls back to the container itself when the dialog has no focusable control
 * yet -- a confirmation modal whose only button is behind a `v-if`, or a modal
 * whose body is still loading. Focusing the container rather than giving up
 * matters: it is what stops the very next Tab from resuming in the page behind
 * the dialog. The container is given `tabindex="-1"` so it can take focus
 * programmatically without joining the Tab sequence itself.
 *
 * Returns the element that ended up focused, or null when there was nothing to
 * focus -- the caller uses that to decide whether to try again once async
 * content has arrived.
 *
 * @param {HTMLElement|null} rootEl
 * @returns {HTMLElement|null}
 */
export const focusFirstElement = (rootEl) => {
  if (!rootEl) {
    return null
  }
  const focusable = getFocusableElements(rootEl)
  const target = focusable.length > 0 ? focusable[0] : rootEl
  if (target === rootEl && !rootEl.hasAttribute('tabindex')) {
    rootEl.setAttribute('tabindex', '-1')
  }
  target.focus()
  return target
}

/**
 * Hands focus back to `returnFocusTo` when it is still the right thing to do.
 *
 * Two guards, both carried over from `Context.vue`:
 *
 * - Focus must still be inside the dialog. If the user has already clicked or
 *   tabbed somewhere else, pulling them back to the opener is the surprising
 *   thing.
 * - `returnFocusTo.isConnected`, for the case where the opener was removed
 *   from the DOM while the dialog was open. That is not exotic here: the
 *   commonest modal in the app is a delete confirmation opened from a row's
 *   context menu, and confirming it destroys the element that opened it.
 *
 * Returns whether focus was actually restored, which is what the tests assert
 * on.
 *
 * @param {HTMLElement|null} rootEl         Teleported dialog root.
 * @param {HTMLElement|null} returnFocusTo  Element captured at open.
 * @returns {boolean}
 */
export const restoreFocus = (rootEl, returnFocusTo) => {
  if (!(returnFocusTo instanceof HTMLElement) || !returnFocusTo.isConnected) {
    return false
  }
  if (!(rootEl instanceof HTMLElement)) {
    return false
  }
  // `contains` is true for the root itself, which is the case after
  // `focusFirstElement` fell back to the container.
  if (!rootEl.contains(document.activeElement)) {
    return false
  }
  returnFocusTo.focus()
  return true
}

/**
 * Wraps Tab and Shift+Tab around the dialog's focusable elements.
 *
 * Returns true when the key was consumed, so the caller can tell whether the
 * event still belongs to anything else.
 *
 * Only the two edges are handled; every Tab in between is left to the browser,
 * which already does it correctly and knows about things this module's
 * selector does not, such as shadow roots and the browser's own
 * platform-specific ordering.
 *
 * When focus is not inside the dialog at all -- it was never moved in, or
 * something stole it -- Tab pulls it back to the first element rather than
 * being ignored. That is the case `aria-modal` claims is impossible, so
 * letting Tab continue in the background would break the promise the role
 * makes.
 *
 * @param {KeyboardEvent} event
 * @param {HTMLElement|null} rootEl
 * @returns {boolean}
 */
export const handleDialogTabKey = (event, rootEl) => {
  if (event.key !== 'Tab') {
    return false
  }
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return false
  }
  if (!rootEl) {
    return false
  }

  const focusable = getFocusableElements(rootEl)
  if (focusable.length === 0) {
    // Nothing to cycle between, but Tab must still not escape into the page
    // behind the dialog.
    event.preventDefault()
    return true
  }

  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement

  if (!rootEl.contains(active)) {
    event.preventDefault()
    first.focus()
    return true
  }

  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
    return true
  }

  if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
    return true
  }

  return false
}

/**
 * Headings that can serve as a dialog's accessible name, most specific first.
 *
 * Derived from a survey of all 85 call sites rather than chosen: 58 of them
 * open their content with an `<h2 class="box__title">`, another 5 wrap that
 * class round an inner `<h2>` (the row-modal family), and the sidebar-style
 * modals title themselves with `.modal-sidebar__title` or
 * `.modal-sidebar__head-name`. A plain leading `<h1>`-`<h3>` catches the
 * handful that use bare headings.
 */
const LABEL_SELECTORS = [
  '.box__title',
  '.modal-sidebar__title',
  '.modal-sidebar__head-name',
  '.import-modal__title',
  'h1, h2, h3',
]

/**
 * Finds the heading that already names this dialog and returns its id,
 * assigning one if it has none.
 *
 * Detection rather than a required prop, for the reason `Context.vue` detects
 * menus instead of taking an opt-in: a prop would have meant touching 85 call
 * sites, and would have left the name and the markup free to drift -- a call
 * site could pass `ariaLabel="Create view"` over a heading that says something
 * else, and nobody would notice, because only a screen reader would ever hear
 * the difference. Reading the rendered heading means the accessible name is
 * the visible name by construction, which is also what WCAG 2.5.3 asks for.
 *
 * Returns null when the dialog renders no heading. The caller then falls back
 * to the `ariaLabel` prop, and if that is absent too, to no name at all --
 * deliberately, because a dialog announced as the generic word "Dialog" is
 * barely better than an unnamed one and would mask the gap from any later
 * audit.
 *
 * A heading with no id of its own is given one derived from its position
 * within the dialog, not from a counter. Both a module-level counter and
 * Vue's `useId()` are shared process-wide, so the value they produce depends
 * on how many other components happened to mount first -- invisible in the
 * browser, but it makes the id move between test runs and would bake a
 * shifting value into every snapshot containing a modal. A positional id is
 * stable for a given dialog, and only ever has to be unique among the modals
 * open at one time.
 *
 * @param {HTMLElement|null} rootEl
 * @returns {string|null}
 */
export const findLabelId = (rootEl) => {
  if (!rootEl) {
    return null
  }
  for (const selector of LABEL_SELECTORS) {
    const heading = rootEl.querySelector(selector)
    if (!heading) {
      continue
    }
    // An empty heading is not a name. This happens while an async title is
    // still resolving, and naming the dialog after the empty string would be
    // worse than leaving it unnamed, because `aria-labelledby` wins over
    // every other naming route.
    if (heading.textContent.trim() === '') {
      continue
    }
    if (!heading.id) {
      // Index among all open dialogs, so two modals stacked at once cannot
      // collide on the same id.
      const openDialogs = Array.from(
        document.querySelectorAll('.modal__box[role="dialog"]')
      )
      const dialogIndex = Math.max(openDialogs.indexOf(rootEl), 0)
      heading.id = `modal-title-${dialogIndex}`
    }
    return heading.id
  }
  return null
}
